import * as React from 'react';
import { RaRecord, useRecordContext } from 'react-admin';
import { Link, Tooltip } from '@mui/material';

/** Record shape required by {@link UrlFormatter}: any record with a `statusCode`/`name`/`rename_Event` and the field(s) referenced by `href`/`source`. */
interface UrlFormatterRecord extends RaRecord {
    statusCode?: string;
    name?: string;
    rename_Event?: string;
    [key: string]: unknown;
}

/** Props for {@link UrlFormatter}. */
interface UrlFormatterProps {
    /** Record field holding the URL to link to. */
    href: string;
    /** Record field to render as the link text (special-cased for `'name'`). */
    source: string;
    /** Record to read from. Falls back to the surrounding `RecordContext` when omitted. */
    record?: UrlFormatterRecord;
}

/**
 * Renders `record[source]` as a link to `record[href]`.
 *
 * Special-cases `source === 'name'`: if `record.rename_Event` is set, the link text and tooltip
 * show the renamed value while the tooltip notes the original name. Also flags records whose
 * `statusCode` is `'Temporarily Unmap'` with an orange link and a tooltip note.
 *
 * @example
 * <UrlFormatter source="name" href="eventUrl" />
 */
const UrlFormatter: React.FC<UrlFormatterProps> = ({ href, source, record, ...props }) => {
    const _record = record || useRecordContext<UrlFormatterRecord>(props) as UrlFormatterRecord;
    const isTemporarily = _record.statusCode === 'Temporarily Unmap';
    return (
        <Tooltip title={`${isTemporarily ? _record.statusCode + ' ' : ''}` + (source === 'name' && _record.rename_Event !== '' ? `Renamed from "${_record.name}"` : '')}>
            <Link style={{ minWidth: 100, color: (source === 'name' && _record.rename_Event !== '') || isTemporarily ? 'orange' : '' }}
                underline="hover" href={_record[href] as string} target="_blank">
                {source !== 'name' ? (_record[source] as React.ReactNode) : (_record.rename_Event || _record.name)}
            </Link>
        </Tooltip>
    );
};

export default UrlFormatter;
